// Image protection utilities to prevent downloading and screenshots

/**
 * Disable right-click context menu on images
 */
export const disableRightClick = () => {
  const handleContextMenu = (e: MouseEvent) => {
    if (e.target instanceof HTMLImageElement) {
      e.preventDefault();
      return false;
    }
  };

  document.addEventListener('contextmenu', handleContextMenu);
  return () => document.removeEventListener('contextmenu', handleContextMenu);
};

/**
 * Disable common keyboard shortcuts for saving/downloading
 */
export const disableSaveShortcuts = () => {
  const handleKeyDown = (e: KeyboardEvent) => {
    // Disable F12, Ctrl+Shift+I, Ctrl+U, Ctrl+S, Ctrl+P, Ctrl+A
    if (
      e.key === 'F12' ||
      (e.ctrlKey && e.shiftKey && e.key === 'I') ||
      (e.ctrlKey && e.key === 'u') ||
      (e.ctrlKey && e.key === 's') ||
      (e.ctrlKey && e.key === 'p') ||
      (e.ctrlKey && e.key === 'a')
    ) {
      e.preventDefault();
      return false;
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  return () => document.removeEventListener('keydown', handleKeyDown);
};

/**
 * Disable text selection on images
 */
export const disableTextSelection = () => {
  const style = document.createElement('style');
  style.textContent = `
    img {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
    }
    
    .gallery-modal img,
    .gallery-image,
    .mosaic-image {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
      pointer-events: none;
    }
    
    /* Disable drag and drop */
    img {
      -webkit-user-drag: none;
      -khtml-user-drag: none;
      -moz-user-drag: none;
      -o-user-drag: none;
      user-drag: none;
    }
  `;
  
  document.head.appendChild(style);
  return () => document.head.removeChild(style);
};

/**
 * Add watermark overlay to images - DISABLED
 */
export const addWatermarkOverlay = (imageElement: HTMLImageElement) => {
  // Watermark functionality disabled - no watermark added
  return;
};

/**
 * Add CSS to prevent screenshot tools
 */
export const addScreenshotProtection = () => {
  const style = document.createElement('style');
  style.textContent = `
    /* Add subtle noise to make screenshots less useful */
    .gallery-modal::before,
    .mosaic-image::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.02) 0%, transparent 50%),
        radial-gradient(circle at 75% 75%, rgba(0,0,0,0.02) 0%, transparent 50%);
      pointer-events: none;
      z-index: 5;
    }
    
    /* Disable print styles */
    @media print {
      .gallery-modal,
      .mosaic-image,
      .gallery-image {
        display: none !important;
      }
    }
  `;
  
  document.head.appendChild(style);
  return () => document.head.removeChild(style);
};

/**
 * Monitor for developer tools and warn users
 */
export const detectDevTools = () => {
  let devtools = false;
  
  const checkDevTools = () => {
    const threshold = 160;
    if (window.outerHeight - window.innerHeight > threshold || 
        window.outerWidth - window.innerWidth > threshold) {
      if (!devtools) {
        devtools = true;
        console.warn('⚠️ Developer tools detected. Please respect copyright.');
        // You could show a modal here if desired
      }
    } else {
      devtools = false;
    }
  };
  
  const interval = setInterval(checkDevTools, 500);
  return () => clearInterval(interval);
};

/**
 * Initialize all image protection measures
 */
export const initializeImageProtection = () => {
  console.log('🛡️ Initializing image protection...');
  
  // Apply protection to existing images
  const applyProtectionToImages = () => {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (img.src && !img.src.includes('placeholder')) {
        addWatermarkOverlay(img);
      }
    });
  };
  
  // Apply protection immediately
  applyProtectionToImages();
  
  // Apply protection to dynamically loaded images
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          const images = node.querySelectorAll('img');
          images.forEach(img => {
            if (img.src && !img.src.includes('placeholder')) {
              addWatermarkOverlay(img);
            }
          });
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Cleanup function
  return () => {
    observer.disconnect();
  };
};

/**
 * Remove all protection measures
 */
export const removeImageProtection = () => {
  // Remove watermarks (if any)
  const watermarks = document.querySelectorAll('.image-watermark');
  watermarks.forEach(watermark => watermark.remove());
  
  // Remove styles
  const watermarkStyles = document.querySelector('#watermark-styles');
  if (watermarkStyles) {
    watermarkStyles.remove();
  }
  
  console.log('🛡️ Image protection removed');
};
